import { TFunction } from "i18next";
import * as yup from "yup";

// Contract Tab Schema
export const contractTabSchema = (t: TFunction) =>
  yup.object({
    fleetNumber: yup.string().nullable().required(t("ila.validation.required")),
    insurance: yup.object({
      contractStartDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .required(t("ila.validation.required"))
        .typeError(t("ila.validation.dateRequired"))
        .min(
          new Date(new Date().setHours(0, 0, 0, 0)),
          t("ila.validation.contractStartDateTodayOrFuture")
        ),
      startDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .required(t("ila.validation.required"))
        .typeError(t("ila.validation.dateRequired"))
        .test(
          "startDate-after-contractStartDate",
          t("ila.validation.startDateAfterContractStartDate"),
          function (value) {
            const { contractStartDate } = this.parent;
            if (!value || !contractStartDate) return true;
            return value >= contractStartDate;
          }
        ),
    }),
    leasing: yup.object({
      leasingContractNumber: yup
        .string()
        .nullable()
        .max(50, t("ila.validation.maxLength", { max: 50 })),
      replacedContractNumber: yup
        .string()
        .nullable()
        .max(50, t("ila.validation.maxLength", { max: 50 })),
    }),
    // --- Add vehicle fields to contract section validation ---
    vehicle: yup.object({
      value: yup
        .number()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .typeError(t("ila.validation.required"))
        .required(t("ila.validation.required"))
        .positive(t("ila.validation.positiveNumber"))
        .max(100000000, t("ila.validation.maxVehicleValue")),
      leaseType: yup.string().nullable(),
      financingShare: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .min(0, t("ila.validation.minPercentage"))
        .max(100, t("ila.validation.maxPercentage")),
      leaseFixedRate: yup.string().nullable(),
    }),
    // --------------------------------------------------------
  });

// Client Tab Schema
export const clientTabSchema = (t: TFunction) =>
  yup.object({
    clientType: yup
      .string()
      .oneOf(["obcan", "osvc", "pravnicka_osoba"])
      .required(t("ila.validation.clientTypeRequired")),

    client: yup.object({
      // Company fields (pravnicka_osoba, osvc)
      code: yup.string().when("$clientType", {
        is: (clientType: string) =>
          clientType === "pravnicka_osoba" || clientType === "osvc",
        then: (schema) => schema.required(t("ila.validation.required")),
        otherwise: (schema) => schema.strip(),
      }),
      name: yup.string().when("$clientType", {
        is: (clientType: string) =>
          clientType === "pravnicka_osoba" || clientType === "osvc",
        then: (schema) =>
          schema.required(t("ila.validation.companyNameRequired")),
        otherwise: (schema) => schema.strip(),
      }),
      vatPayer: yup.string().when("$clientType", {
        is: (clientType: string) =>
          clientType === "pravnicka_osoba" || clientType === "osvc",
        then: (schema) => schema.required(t("ila.validation.required")),
        otherwise: (schema) => schema.strip(),
      }),

      // Individual/OSVC fields (obcan, osvc)
      firstName: yup.string().when("$clientType", {
        is: (clientType: string) =>
          clientType === "obcan" || clientType === "osvc",
        then: (schema) =>
          schema.required(t("ila.validation.firstNameRequired")),
        otherwise: (schema) => schema.strip(),
      }),
      lastName: yup.string().when("$clientType", {
        is: (clientType: string) =>
          clientType === "obcan" || clientType === "osvc",
        then: (schema) => schema.required(t("ila.validation.lastNameRequired")),
        otherwise: (schema) => schema.strip(),
      }),
      birthNumber: yup.string().when("$clientType", {
        is: (clientType: string) =>
          clientType === "obcan" || clientType === "osvc",
        then: (schema) => schema.required(t("ila.validation.required")),
        otherwise: (schema) => schema.strip(),
      }),

      address: yup.object({
        street: yup.string().required(t("ila.validation.required")),
        house: yup.string().required(t("ila.validation.required")),
        flat: yup.string().nullable(),
        index: yup.string().required(t("ila.validation.required")),
        city: yup.string().required(t("ila.validation.required")),
        suburb: yup.string().nullable(),
        phone: yup.string().nullable(),
        email: yup.string().nullable().email(t("ila.validation.email")),
        mobilePhone: yup.string().nullable(),
      }),
      correspondenceAddress: yup.object({
        street: yup.string().when("$correspondenceAddressDifferent", {
          is: true,
          then: (schema) => schema.required(t("ila.validation.required")),
          otherwise: (schema) => schema.nullable(),
        }),
        house: yup.string().when("$correspondenceAddressDifferent", {
          is: true,
          then: (schema) => schema.required(t("ila.validation.required")),
          otherwise: (schema) => schema.nullable(),
        }),
        flat: yup.string().nullable(),

        index: yup.string().when("$correspondenceAddressDifferent", {
          is: true,
          then: (schema) => schema.required(t("ila.validation.required")),
          otherwise: (schema) => schema.nullable(),
        }),
        city: yup.string().when("$correspondenceAddressDifferent", {
          is: true,
          then: (schema) => schema.required(t("ila.validation.required")),
          otherwise: (schema) => schema.nullable(),
        }),
        suburb: yup.string().nullable(),
        phone: yup.string().nullable(),
        email: yup.string().nullable().email(t("ila.validation.email")),
        mobilePhone: yup.string().nullable(),
      }),

      // Legal representative (contactPerson) is not required for any type in the UI, so keep as nullable
      contactPerson: yup
        .object({
          order: yup.string().nullable(),
          firstName: yup.string().nullable(),
          lastName: yup.string().nullable(),
          function: yup.string().nullable(),
        })
        .nullable(),
    }),

    correspondenceAddressDifferent: yup.boolean(),

    elkom: yup
      .string()
      .oneOf(["A", "N"])
      .required(t("ila.validation.elkomRequired")),
    marketing: yup
      .string()
      .oneOf(["V", "A", "N"])
      .required(t("ila.validation.marketingRequired")),
  });

// Vehicle Tab Schema
export const vehicleTabSchema = (t: TFunction) =>
  yup.object({
    vehicle: yup.object({
      vin: yup
        .string()
        .required(t("ila.validation.required"))
        .matches(/^[A-HJ-NPR-Z0-9]{17}$/, t("ila.validation.vinFormat"))
        .length(17, t("ila.validation.vinLength")),
      licensePlate: yup
        .string()
        .required(t("ila.validation.required"))
        .max(15, t("ila.validation.maxLength", { max: 15 })),
      registrationCertificateNumber: yup
        .string()
        .nullable()
        .max(30, t("ila.validation.maxLength", { max: 30 })),
      brand: yup.string().required(t("ila.validation.required")),
      model: yup.string().nullable(),
      fuel: yup.string().nullable(),
      vehicleCategory: yup.string().required(t("ila.validation.required")),
      vehicleUsage: yup.string().required(t("ila.validation.required")),
      registrationType: yup.string().nullable(),
      countryCode: yup
        .string()
        .nullable()
        .matches(/^[A-Z]{2,3}$/, t("ila.validation.countryCode")),
      manufactureDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .max(new Date(), t("ila.validation.manufactureDateInFuture")),
      firstRegistrationDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .max(new Date(), t("ila.validation.registrationDateInFuture"))
        .test(
          "registration-after-manufacture",
          t("ila.validation.registrationAfterManufacture"),
          function (value) {
            const { manufactureDate } = this.parent;
            if (!value || !manufactureDate) return true;
            return value >= manufactureDate;
          }
        ),
      powerKw: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .positive(t("ila.validation.positiveNumber"))
        .max(1000, t("ila.validation.maxPower")),
      cubicCapacityCcm: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .positive(t("ila.validation.positiveNumber"))
        .integer()
        .max(10000, t("ila.validation.maxCubicCapacity")),
      weight: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .positive(t("ila.validation.positiveNumber"))
        .max(50000, t("ila.validation.maxWeight")),
      seats: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .positive()
        .integer()
        .min(1, t("ila.validation.minSeats"))
        .max(100, t("ila.validation.maxSeats")),
      mileage: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .min(0, t("ila.validation.minMileage"))
        .max(9999999, t("ila.validation.maxMileage")),
      value: yup
        .number()
        .required(t("ila.validation.required"))
        .positive(t("ila.validation.positiveNumber"))
        .max(100000000, t("ila.validation.maxVehicleValue")),
      damagedBefore: yup.string().nullable(),
      repaired: yup.string().nullable(),
      registeredAbroad: yup.string().nullable(),
      twoYearRegistration: yup.string().nullable(),
      leaseType: yup.string().nullable(),
      financingShare: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .min(0, t("ila.validation.minPercentage"))
        .max(100, t("ila.validation.maxPercentage")),
      leaseFixedRate: yup.string().nullable(),
      owner: yup
        .object({
          code: yup.string().nullable(),
          name: yup.string().nullable(),
          lastName: yup.string().nullable(),
          firstName: yup.string().nullable(),
          birthDate: yup
            .date()
            .nullable()
            .transform((curr, orig) => (orig === "" ? null : curr)),
          company: yup.boolean(),
        })
        .nullable(),
    }),
    // Kooperativa fields that depend on vehicle
    kooperativaFields: yup.object({
      damageSpecification: yup.string().when(["$data.vehicle.damagedBefore"], {
        is: (damagedBefore: string) => damagedBefore === "A",
        then: (schema) =>
          schema.required(t("ila.validation.damageSpecificationRequired")),
        otherwise: (schema) => schema.nullable(),
      }),
      minorDamageSpecification: yup.string().when(["$data.vehicle.repaired"], {
        is: (repaired: string) => repaired === "A",
        then: (schema) =>
          schema.required(t("ila.validation.minorDamageSpecificationRequired")),
        otherwise: (schema) => schema.nullable(),
      }),
      standingPlaces: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .min(0)
        .integer(),
      numberOfBeds: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .min(0)
        .integer(),
      notOperational: yup.string().nullable(),
      technicalCertificateNumber: yup.string().nullable(),
    }),
  });

// Coverages Tab Schema
export const coveragesTabSchema = (t: TFunction) =>
  yup.object({
    selectedProducts: yup
      .array()
      .of(yup.string())
      .min(1, t("ila.validation.selectAtLeastOneProduct")),
    selectedInsurers: yup
      .array()
      .of(yup.string())
      .min(1, t("ila.validation.selectAtLeastOneInsurer")),
    insurance: yup.object({
      endDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .when(["$data.insurance.startDate"], {
          is: (startDate: Date) => startDate != null,
          then: (schema) =>
            schema.min(
              yup.ref("$data.insurance.startDate"),
              t("ila.validation.endDateAfterStartDate")
            ),
          otherwise: (schema) => schema,
        }),
      payments: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .positive(t("ila.validation.positiveNumber"))
        .integer(t("ila.validation.integerRequired"))
        .min(1, t("ila.validation.minPayments"))
        .max(12, t("ila.validation.maxPayments")),
      coverageTerritory: yup.string().required(t("ila.validation.required")),
      currency: yup.string().required(t("ila.validation.required")),
      driverBirthDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .max(new Date(), t("ila.validation.birthDateInFuture")),
      driverAge: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .positive()
        .integer()
        .min(18, t("ila.validation.minDriverAge"))
        .max(99, t("ila.validation.maxDriverAge")),
      driverLicenceDate: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .max(new Date(), t("ila.validation.licenceDateInFuture"))
        .when(["$data.insurance.driverBirthDate"], {
          is: (birthDate: Date) => birthDate != null,
          then: (schema) =>
            schema.min(
              yup.ref("$data.insurance.driverBirthDate"),
              t("ila.validation.licenceAfterBirth")
            ),
          otherwise: (schema) => schema,
        }),
      driverExperience: yup
        .number()
        .nullable()
        .transform((curr, orig) => (orig === "" ? null : curr))
        .min(0, t("ila.validation.minExperience"))
        .integer()
        .max(80, t("ila.validation.maxExperience")),
    }),
    kooperativaFields: yup.object({
      preferredLiquidation: yup.string().nullable(),
      specialAgreement: yup.string().nullable(),
      gainers: yup.array().of(
        yup.object({
          order: yup.string().required(t("ila.validation.required")),
          code: yup.string().required(t("ila.validation.required")),
          userId: yup.string().nullable(),
          personalNumber: yup.string().nullable(),
          isSigning: yup.boolean(),
        })
      ),
      risks: yup.array().of(
        yup.object({
          idRisk: yup.string().required(t("ila.validation.required")),
          code: yup.string().required(t("ila.validation.required")),
          valueType: yup.string().required(t("ila.validation.required")),
          value: yup.string().required(t("ila.validation.required")),
        })
      ),
    }),
  });

// Tab definitions
export const TAB_SCHEMAS = {
  contract: contractTabSchema,
  client: clientTabSchema,
  vehicle: vehicleTabSchema,
  coverages: coveragesTabSchema,
} as const;

export type TabName = keyof typeof TAB_SCHEMAS;

// Get schema for a specific tab
export const getTabSchema = (tabName: TabName, t: TFunction) => {
  const schemaFactory = TAB_SCHEMAS[tabName];
  return schemaFactory ? schemaFactory(t) : yup.object();
};

// Full form schema (combines all tabs)
export const getFullSchema = (t: TFunction) => {
  // Create individual schemas
  const contractTab = contractTabSchema(t);
  const clientTab = clientTabSchema(t);
  const vehicleTab = vehicleTabSchema(t);
  const coveragesTab = coveragesTabSchema(t);

  // Extract insurance fields from both schemas
  const contractInsuranceSchema = contractTab.fields
    .insurance as yup.ObjectSchema<any>;
  const coveragesInsuranceSchema = coveragesTab.fields
    .insurance as yup.ObjectSchema<any>;

  // Extract kooperativa fields from both schemas
  const vehicleKooperativaSchema = vehicleTab.fields
    .kooperativaFields as yup.ObjectSchema<any>;
  const coveragesKooperativaSchema = coveragesTab.fields
    .kooperativaFields as yup.ObjectSchema<any>;

  return yup.object({
    // Contract tab fields
    fleetNumber: contractTab.fields.fleetNumber,
    // Client tab fields - use single client
    clientType: clientTab.fields.clientType,
    client: clientTab.fields.client,
    correspondenceAddressDifferent:
      clientTab.fields.correspondenceAddressDifferent,
    elkom: clientTab.fields.elkom,
    marketing: clientTab.fields.marketing,
    // Vehicle tab fields
    vehicle: vehicleTab.fields.vehicle,
    // Insurance fields (split across tabs)
    insurance: yup.object({
      ...contractInsuranceSchema.fields,
      ...coveragesInsuranceSchema.fields,
    }),
    // Leasing fields
    leasing: contractTab.fields.leasing,
    // Kooperativa fields (split across tabs)
    kooperativaFields: yup.object({
      ...vehicleKooperativaSchema.fields,
      ...coveragesKooperativaSchema.fields,
    }),
    // Coverage selection
    selectedProducts: coveragesTab.fields.selectedProducts,
    selectedInsurers: coveragesTab.fields.selectedInsurers,
  });
};

// Validate specific tab
export const validateTab = async (
  tabName: TabName,
  data: any,
  t: TFunction
): Promise<{ isValid: boolean; errors: any }> => {
  const schema = getTabSchema(tabName, t);

  try {
    await schema.validate(data, {
      abortEarly: false,
      context: {
        ...data,
        clientType: data.clientType, // Pass clientType for conditional validation
        correspondenceAddressDifferent: data.correspondenceAddressDifferent, // Pass for conditional validations
      }, // Pass full data for cross-field validation
    });
    return { isValid: true, errors: {} };
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors = error.inner.reduce((acc, err) => {
        if (err.path) {
          const pathParts = err.path.split(".");
          let current = acc;

          pathParts.forEach((part, index) => {
            if (index === pathParts.length - 1) {
              current[part] = err.message;
            } else {
              current[part] = current[part] || {};
              current = current[part];
            }
          });
        }
        return acc;
      }, {} as any);

      return { isValid: false, errors };
    }

    throw error;
  }
};

// Get errors for a specific tab
export const getTabErrors = (
  errors: any,
  tabName: TabName
): Record<string, any> => {
  switch (tabName) {
    case "contract":
      return {
        fleetNumber: errors.fleetNumber,
        insurance: errors.insurance
          ? {
              contractStartDate: errors.insurance.contractStartDate,
              startDate: errors.insurance.startDate,
            }
          : {},
        leasing: errors.leasing || {},
      };
    case "client":
      return {
        clientType: errors.clientType,
        client: errors.client || {},
        correspondenceAddressDifferent: errors.correspondenceAddressDifferent,
        elkom: errors.elkom,
        marketing: errors.marketing,
      };
    case "vehicle":
      return {
        vehicle: errors.vehicle || {},
        kooperativaFields: errors.kooperativaFields
          ? {
              damageSpecification: errors.kooperativaFields.damageSpecification,
              minorDamageSpecification:
                errors.kooperativaFields.minorDamageSpecification,
              standingPlaces: errors.kooperativaFields.standingPlaces,
              numberOfBeds: errors.kooperativaFields.numberOfBeds,
              notOperational: errors.kooperativaFields.notOperational,
              technicalCertificateNumber:
                errors.kooperativaFields.technicalCertificateNumber,
            }
          : {},
      };
    case "coverages":
      return {
        selectedProducts: errors.selectedProducts,
        selectedInsurers: errors.selectedInsurers,
        insurance: errors.insurance
          ? {
              endDate: errors.insurance.endDate,
              payments: errors.insurance.payments,
              coverageTerritory: errors.insurance.coverageTerritory,
              currency: errors.insurance.currency,
              driverBirthDate: errors.insurance.driverBirthDate,
              driverAge: errors.insurance.driverAge,
              driverLicenceDate: errors.insurance.driverLicenceDate,
              driverExperience: errors.insurance.driverExperience,
            }
          : {},
        kooperativaFields: errors.kooperativaFields
          ? {
              preferredLiquidation:
                errors.kooperativaFields.preferredLiquidation,
              specialAgreement: errors.kooperativaFields.specialAgreement,
              gainers: errors.kooperativaFields?.gainers,
              risks: errors.kooperativaFields?.risks,
            }
          : {},
      };
    default:
      return {};
  }
};

// Check if a tab has any errors
export const hasTabErrors = (errors: any, tabName: TabName): boolean => {
  const tabErrors = getTabErrors(errors, tabName);

  const checkErrors = (obj: any): boolean => {
    if (!obj) return false;

    for (const key in obj) {
      const value = obj[key];
      if (value) {
        if (typeof value === "string") return true;
        if (Array.isArray(value) && value.length > 0) return true;
        if (typeof value === "object" && checkErrors(value)) return true;
      }
    }

    return false;
  };

  return checkErrors(tabErrors);
};

// Resolver function helper for react-hook-form
export const useYupValidationResolver = (schema: any) => {
  return async (data: any) => {
    try {
      const values = await schema.validate(data, {
        abortEarly: false,
        context: { data },
      });

      return {
        values,
        errors: {},
      };
    } catch (errors) {
      if (!(errors instanceof yup.ValidationError)) {
        return {
          values: {},
          errors: {
            [errors instanceof Error ? errors.message : "validation"]: {
              type: "validation",
              message:
                errors instanceof Error ? errors.message : String(errors),
            },
          },
        };
      }

      return {
        values: {},
        errors: errors.inner.reduce(
          (allErrors, currentError) => ({
            ...allErrors,
            [currentError.path || "validation"]: {
              type: currentError.type ?? "validation",
              message: currentError.message,
            },
          }),
          {}
        ),
      };
    }
  };
};

// Main export - the schema used in ItemPage
export const useItemPageValidationSchema = (yup: any, t: TFunction) => {
  return getFullSchema(t);
};
